import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type ComponentType,
  type ReactNode,
} from 'react';
import type { IncidentRiskSource } from './features/incidents-emergency/victim-risk-reassessment-section';
import {
  AlertTriangle,
  Bell,
  BookOpenCheck,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Download,
  FileCheck2,
  FireExtinguisher,
  FlaskConical,
  FolderOpen,
  HardHat,
  HeartPulse,
  Home,
  LayoutGrid,
  LogOut,
  Menu,
  MessageSquareWarning,
  Moon,
  NotebookPen,
  Palette,
  Plus,
  Search,
  Settings,
  Star,
  Sun,
  ThermometerSun,
  Truck,
  UsersRound,
  WalletCards,
  X,
} from 'lucide-react';

import { Button } from '@kop/ui';
import { OperationsDashboard } from './features/briefing/operations-dashboard';
import { HeadquartersDashboard } from './features/briefing/headquarters-dashboard';
import { createHeadquartersReadContext, headquartersWorkScope, type HeadquartersReadContext } from './runtime/headquarters-read-context';
import {
  dueActionCountOf,
  pendingApprovalCountOf,
  unresolvedFindingCountOf,
  useWorkHomeProjection,
  type WorkHomeSnapshot,
} from './features/briefing/work-home-projection';
import { navigationBadgeOf, type NavigationBadge } from './navigation-badges';
import './features/briefing/briefing.css';
import { safetyBoardAuthority, type SafetyBoardAuthority } from './features/safety-board/model';
import { getTrustedMediaAdapter } from './features/media/trusted-media-adapter';
import {
  getApprovalAuthority,
  canOpenApprovalHub,
} from './features/approval-hub/approval-data-adapter';
import { FeedbackDialog } from './features/feedback/feedback-dialog';
import {
  operationalSelectedScopeId,
  workspaceHomeSelectedScopeId,
} from './runtime/operational-selected-scope';
import { usePwa } from './runtime/pwa-context';
import { FeatureLoading, FeatureSkeleton } from './features/feature-skeleton';
import { FieldMobileShell, type FieldChecklistKind } from './features/field-mobile/field-mobile-shell';
import { isFieldMobileFeature } from './features/field-mobile/field-mobile-model';
import { mobileSessionMediaQuery } from './auth/mobile-session-lock-model';
import { HubEmpty, HubFilters, HubHeader } from './features/hub-shell/hub-shell';
import './features/operations.css';
import { canManageContractorDirectory, canViewContractorDirectory } from './features/contractors/contractor-access';
import type { InspectionStartPreset } from './features/inspection-read-model/inspection-read-workspace';
import type { InspectionFindingPreset } from './features/inspection-read-model/dashboard-port';
import type { FindingCenterPreset } from './features/inspection/finding-center-model';
import {
  loadAccountSettingsModule,
  loadInspectionDashboardModule,
  loadInspectionWorkflowModule,
  prefetchEntitledRoutes,
  prefetchFeatureRoute,
} from './feature-loaders';
import { normalizeWorkspacePath } from './workspace-route-policy';
import {
  canDiscoverWorkspaceFeature,
  canManageSystemFeedback,
} from './workspace-feature-entitlements';

const TbmWorkspace = lazy(() =>
  import('./features/tbm/tbm-workspace').then((module) => ({
    default: module.TbmWorkspace,
  })),
);
const TbmSettingsWorkspace = lazy(() =>
  import('./features/tbm/tbm-settings-workspace').then((module) => ({
    default: module.TbmSettingsWorkspace,
  })),
);
const TbmDisplaySettingsWorkspace = lazy(() =>
  import('./features/tbm/tbm-display-settings').then(module => ({ default: module.TbmDisplaySettingsWorkspace })),
);

const SharedDriveWorkspace = lazy(() =>
  import('./features/shared-drive/shared-drive-workspace').then((module) => ({
    default: module.SharedDriveWorkspace,
  })),
);

const InspectionWorkflow = lazy(() =>
  loadInspectionWorkflowModule().then((module) => ({
    default: module.InspectionWorkflow,
  })),
);

const ContractorWorkspace = lazy(() =>
  import('./features/contractors/contractor-workspace').then((module) => ({
    default: module.ContractorWorkspace,
  })),
);
const WorkLedgerWorkspace = lazy(() =>
  import('./features/work-control/work-ledger-workspace').then((module) => ({
    default: module.WorkLedgerWorkspace,
  })),
);
const SafetyEducationWorkspace = lazy(() =>
  import('./features/safety-education/safety-education-workspace').then((module) => ({
    default: module.SafetyEducationWorkspace,
  })),
);
const SafetyContentWorkspace = lazy(() =>
  import('./features/safety-content/safety-content-workspace').then((module) => ({
    default: module.SafetyContentWorkspace,
  })),
);
const FeedbackWorkspace = lazy(() =>
  import('./features/feedback/feedback-workspace').then((module) => ({
    default: module.FeedbackWorkspace,
  })),
);
const EmergencyDrillWorkspace = lazy(() =>
  import('./features/incidents-emergency/emergency-drill-workspace').then((module) => ({
    default: module.EmergencyDrillWorkspace,
  })),
);
const VictimRegistryWorkspace = lazy(() =>
  import('./features/incidents-emergency/victim-registry-workspace').then((module) => ({
    default: module.VictimRegistryWorkspace,
  })),
);
const RiskAssessmentWorkspace = lazy(() =>
  import('./features/risk-assessment/risk-assessment-workspace').then((module) => ({
    default: module.RiskAssessmentWorkspace,
  })),
);
const BudgetMaterialsWorkspace = lazy(() =>
  import('./features/budget-materials/budget-materials-workspace').then((module) => ({
    default: module.BudgetMaterialsWorkspace,
  })),
);
const MeetingMinutesWorkspace = lazy(() =>
  import('./features/communications/meeting-minutes-workspace').then((module) => ({
    default: module.MeetingMinutesWorkspace,
  })),
);
const InspectionReadWorkspace = lazy(() =>
  loadInspectionDashboardModule().then((module) => ({
    default: module.InspectionReadWorkspace,
  })),
);
const IdentityAdminWorkspace = lazy(() =>
  import('./features/identity-admin/identity-admin-workspace').then((module) => ({
    default: module.IdentityAdminWorkspace,
  })),
);
const AccountSettingsWorkspace = lazy(() =>
  loadAccountSettingsModule().then((module) => ({
    default: module.AccountSettingsWorkspace,
  })),
);
const MyInfoWorkspace = lazy(() =>
  import('./features/my-info/my-info-workspace').then((module) => ({
    default: module.MyInfoWorkspace,
  })),
);
const InspectionChecklistAdminWorkspace = lazy(() =>
  import('./features/inspection-checklist-admin/inspection-checklist-admin-workspace').then(
    (module) => ({ default: module.InspectionChecklistAdminWorkspace }),
  ),
);
const WorkLocationRegistryWorkspace = lazy(() =>
  import('./features/work-location-registry/work-location-registry-workspace').then((module) => ({
    default: module.WorkLocationRegistryWorkspace,
  })),
);
const ChemicalMsdsWorkspace = lazy(() =>
  import('./features/chemical-msds/chemical-msds-workspace').then((module) => ({
    default: module.ChemicalMsdsWorkspace,
  })),
);
const HeatIndexWorkspace = lazy(() =>
  import('./features/heat-index/heat-index-workspace').then((module) => ({
    default: module.HeatIndexWorkspace,
  })),
);
const EquipmentDispatchWorkspace = lazy(() =>
  import('./features/equipment-dispatch/equipment-dispatch-workspace').then((module) => ({
    default: module.EquipmentDispatchWorkspace,
  })),
);
const OrganizationChartWorkspace = lazy(() =>
  import('./features/organization-chart/organization-administration-workspace').then((module) => ({
    default: module.OrganizationAdministrationWorkspace,
  })),
);
const ApprovalHub = lazy(() =>
  import('./features/approval-hub/approval-hub').then((module) => ({
    default: module.ApprovalHub,
  })),
);
import { equipmentDispatchProjection } from './features/equipment-dispatch/access';
import {
  AuthBoundary,
  type AccessScope,
  type AuthAdapter,
  type AuthenticatedSession,
  type WorkspaceRole,
} from './auth';
import { productScopeRoleLabel } from './auth/product-session';
import { createSystemManagementRepository } from './features/system-management/firebase-repository';

const SystemManagementWorkspace = lazy(() => import('./features/system-management/system-management-workspace')
  .then(module => ({ default: module.SystemManagementWorkspace })));
import { useWorkspaceReady } from './auth/workspace-entry-context';
import { projectWorkspaceNavigation } from './navigation-projection';
import { featureAccessProjectionStatus } from './feature-access-projection';
import { HeadquartersSiteSwitcher } from './headquarters-site-switcher';
import { isHeadquartersScope, switchWorkspaceScope } from './headquarters-site-navigation';
import { clearSensitiveMemoryCaches } from './runtime/sensitive-cache-lifecycle';
import {
  BrowserFavoritePreferenceRepository,
  moveFavorite,
  projectFavoriteIds,
} from './home-favorites';
import {
  SiteAdministrationDialog,
  type SiteAdministrationSection,
} from './site-administration-dialog';

const favoritePreferences = new BrowserFavoritePreferenceRepository();

type Role = WorkspaceRole;
type Capability =
  'inspect' | 'issue' | 'remediate' | 'verify' | 'approve' | 'audit' | 'govern' | 'admin';
type GroupId = 'field' | 'control' | 'people' | 'operations' | 'governance';
type Screen = 'home' | 'feature' | 'mobileHome' | 'inspection';

type Feature = {
  id: string;
  label: string;
  group: GroupId;
  capabilities: Capability[];
  roles?: Role[];
  navigation?: boolean;
};

const roleProfiles: Record<
  Role,
  { label: string; scope: string; capabilities: Capability[]; responsibility: string }
> = {
  client: {
    label: '원청 안전담당',
    scope: '음성공장 · 안전보건팀',
    capabilities: ['inspect', 'issue', 'verify', 'approve', 'govern'],
    responsibility: '점검 · 발행 · 검증',
  },
  partner: {
    label: '협력사 관리자',
    scope: '협력사 · 음성공장',
    capabilities: ['remediate'],
    responsibility: '조치 · 증빙 제출',
  },
  siteLead: {
    label: '사업장 관리자',
    scope: '음성공장 · 관리책임자',
    capabilities: ['approve', 'verify', 'govern'],
    responsibility: '검토 · 승인 · 자원 배정',
  },
  hqSafety: {
    label: '본사 안전관리',
    scope: '본사 · 전 사업장',
    capabilities: ['audit', 'govern', 'approve', 'verify'],
    responsibility: '기준 · 이행 검토',
  },
  hqAudit: {
    label: '본사 조회·감사',
    scope: '본사 · 감사 범위',
    capabilities: ['audit'],
    responsibility: '증빙 · 변경 이력 조회',
  },
  system: {
    label: '전사 관리자',
    scope: '한국오션플랫폼',
    capabilities: ['govern', 'admin', 'audit'],
    responsibility: '전사 설정 · 감독',
  },
};

const groups: Array<{ id: GroupId; label: string; icon: ComponentType<{ size?: number }> }> = [
  { id: 'field', label: '현장 실행', icon: HardHat },
  { id: 'control', label: '작업 통제', icon: ClipboardCheck },
  { id: 'people', label: '교육·설비', icon: BookOpenCheck },
  { id: 'operations', label: '회의·운영', icon: LayoutGrid },
  { id: 'governance', label: '기준·보고', icon: Building2 },
];

const features: Feature[] = [
  {
    id: 'system-management', label: '시스템 관리', group: 'operations',
    capabilities: ['admin'], navigation: false,
  },
  {
    id: 'inspection-log',
    label: '점검일지·특별점검',
    group: 'field',
    capabilities: ['inspect', 'approve', 'audit'],
    navigation: false,
  },
  {
    id: 'inspection-reporting',
    label: '점검',
    group: 'field',
    capabilities: ['inspect', 'remediate', 'verify', 'audit'],
  },
  {
    id: 'inspection-unresolved',
    label: '점검 미조치 현황',
    group: 'field',
    capabilities: ['inspect', 'remediate', 'verify', 'audit'],
    navigation: false,
  },

  {
    id: 'tbm',
    label: 'TBM',
    group: 'field',
    capabilities: ['inspect', 'remediate', 'verify', 'audit'],
  },
  {
    id: 'finding-action',
    label: '지적 및 조치',
    group: 'field',
    capabilities: ['issue', 'remediate', 'verify', 'audit'],
    navigation: false,
  },
  {
    id: 'incident',
    label: '재해자 관리',
    group: 'field',
    capabilities: ['inspect', 'approve', 'audit'],
  },

  {
    id: 'regular-risk',
    label: '위험성평가',
    group: 'control',
    capabilities: ['inspect', 'remediate', 'approve', 'audit'],
  },

  {
    id: 'work-permit',
    label: '비일상작업',
    group: 'control',
    capabilities: ['inspect', 'remediate', 'approve', 'audit'],
  },
  {
    id: 'equipment-dispatch',
    label: '차량·장비 작업배차',
    group: 'control',
    capabilities: ['inspect', 'remediate', 'approve'],
    roles: ['client', 'partner', 'siteLead'],
  },

  {
    id: 'safety-training',
    label: '안전보건교육',
    group: 'people',
    capabilities: ['inspect', 'remediate', 'approve', 'audit'],
  },
  {
    id: 'evacuation-drill',
    label: '비상대피훈련',
    group: 'people',
    capabilities: ['inspect', 'remediate', 'approve', 'audit'],
  },

  {
    id: 'chemical-msds-management',
    label: '화학물질·MSDS 관리대장',
    group: 'people',
    capabilities: ['inspect', 'remediate', 'govern', 'audit'],
    roles: ['client', 'partner', 'siteLead', 'hqSafety', 'hqAudit'],
  },

  {
    id: 'heat-index',
    label: '체감온도 기록',
    group: 'people',
    capabilities: ['inspect', 'remediate', 'audit'],
  },
  {
    id: 'meeting-minutes',
    label: '회의록',
    group: 'operations',
    capabilities: ['inspect', 'remediate', 'approve', 'audit'],
    /* Five unrelated duties were filed under one "communication" screen. Notices are the board's
       job now, and reports reach the top-bar control from anywhere. 회의, 일정 and 마감회의 are
       still only here, so the entry stays until each is its own feature -- removing it first
       left them reachable by typed URL alone. */
  },
  {
    id: 'safety-content',
    label: '안전 콘텐츠 제작',
    group: 'operations',
    capabilities: ['govern'],
    roles: ['client'],
  },
  {
    id: 'electronic-approval',
    label: '전자결재',
    group: 'operations',
    capabilities: ['remediate', 'approve', 'audit'],
    roles: ['client', 'partner', 'siteLead', 'hqSafety', 'hqAudit'],
  },

  {
    id: 'safety-board',
    label: '게시판',
    group: 'operations',
    /* Every duty reaches the board: it is the site's own notice ledger, not a role tool.
       Publishing is a separate entitlement the server issues (see safetyBoardAuthority). */
    capabilities: [
      'inspect',
      'issue',
      'remediate',
      'verify',
      'approve',
      'audit',
      'govern',
      'admin',
    ],
    navigation: false,
  },

  {
    id: 'product-feedback',
    label: '시스템 개선 관리',
    group: 'operations',
    /* Available only through the server-verified developer account entitlement. */
    capabilities: ['govern', 'admin'],
    navigation: false,
  },
  {
    id: 'my-info',
    label: '내 정보',
    group: 'operations',
    capabilities: [
      'inspect',
      'issue',
      'remediate',
      'verify',
      'approve',
      'audit',
      'govern',
      'admin',
    ],
    roles: ['client', 'partner', 'siteLead', 'hqSafety', 'hqAudit'],
    navigation: false,
  },
  {
    id: 'tbm-settings',
    label: 'TBM 설정',
    group: 'operations',
    capabilities: ['inspect', 'remediate'],
    roles: ['partner'],
    navigation: false,
  },
  {
    id: 'user-approval',
    label: '사용자 관리',
    group: 'operations',
    capabilities: ['govern', 'admin'],
    navigation: false,
  },
  {
    id: 'contractor-management',
    label: '협력업체',
    group: 'operations',
    capabilities: ['admin', 'issue'],
    roles: ['client'],
    navigation: false,
  },

  {
    id: 'safety-cost',
    label: '안전관리비',
    group: 'operations',
    capabilities: ['remediate', 'approve', 'govern', 'audit'],
  },

  {
    id: 'site-settings',
    label: '사업장 기본정보',
    group: 'governance',
    capabilities: ['govern', 'admin'],
    navigation: false,
  },
  {
    id: 'inspection-checklists',
    label: '점검 분류·점검표',
    group: 'governance',
    capabilities: ['govern', 'admin'],
    navigation: false,
  },
  {
    id: 'organization-chart',
    label: '조직도',
    group: 'governance',
    capabilities: ['govern', 'admin'],
    navigation: false,
  },
  {
    id: 'work-location-registry',
    label: '공종·작업장소',
    group: 'governance',
    capabilities: ['govern', 'admin'],
    navigation: false,
  },

  {
    id: 'shared-drive',
    label: '안전보건자료실',
    group: 'governance',
    capabilities: ['remediate', 'approve', 'govern', 'audit', 'admin'],
    roles: ['client', 'partner', 'siteLead', 'hqSafety', 'hqAudit'],
  },

];

const topUtilityFeatureIds = new Set(['meeting-minutes', 'electronic-approval', 'shared-drive']);

/* These entries have already been reviewed with the product owner. Everything else that the
   authority projection returns is still rendered below them under a review marker: no live
   feature is hidden merely because its final navigation position has not been discussed yet. */
const reviewedRailFeatureOrder = [
  'inspection-reporting',
  'tbm',
  'work-permit',
  'regular-risk',
  'incident',
  'safety-training',
  'evacuation-drill',
  'safety-cost',
  'chemical-msds-management',
  'safety-content',
] as const;
const bottomRailFeatureOrder = [ 'heat-index', 'equipment-dispatch'] as const;

const directNavigationIcons: Readonly<Record<string, ComponentType<{ size?: number }>>> = {
  'inspection-reporting': ClipboardCheck,
  tbm: UsersRound,
  'work-permit': FileCheck2,
  'equipment-dispatch': Truck,
  'regular-risk': AlertTriangle,
  incident: HeartPulse,
  'safety-training': BookOpenCheck,
  'evacuation-drill': FireExtinguisher,
  'safety-cost': WalletCards,
  'chemical-msds-management': FlaskConical,

  'heat-index': ThermometerSun,
  'safety-content': Palette,
  'meeting-minutes': NotebookPen,
  'electronic-approval': FileCheck2,
  'shared-drive': FolderOpen,

};

/**
 * Site administration.
 *
 * These five are the same `features` entries they always were - the labels,
 * capabilities and roles below are read from that table, so a section is
 * offered on exactly the terms the navigation used to offer it on. They are
 * gathered behind the gear instead of being scattered across menu groups, and
 * carry `navigation: false` so they appear in one place only.
 */
const siteAdministrationSectionIds = [
  'system-management',
  'site-settings',
  'inspection-checklists',
  'organization-chart',
  'work-location-registry',
  'my-info',
  'tbm-settings',
  'user-approval',
  'contractor-management',
] as const;

/** Deep links kept working: each section still answers on its own path. */
const siteAdministrationPaths: Readonly<Record<string, string>> = {
  'system-management': '/admin/system',
  'site-settings': '/admin/settings',
  'inspection-checklists': '/admin/inspection-checklists',
  'organization-chart': '/admin/organization',
  'work-location-registry': '/admin/work-locations',
  'my-info': '/admin/my-info',
  'tbm-settings': '/admin/tbm',
  'user-approval': '/admin/users',
  'contractor-management': '/contractors',
};

const siteAdministrationSectionForPath = (pathname: string) =>
  siteAdministrationSectionIds.find((id) => siteAdministrationPaths[id] === pathname) ?? null;

function SiteAdministrationLoading({ label }: { label: string }) {
  return (
    <div
      className="site-admin-loading"
      role="status"
      aria-label={`${label}: 자료를 불러오고 있습니다.`}
    >
      <FeatureLoading label={`${label} 불러오는 중`} />
    </div>
  );
}

const partnerNavigationFeatureIds = new Set([

  'chemical-msds-management',
  'incident',
  'electronic-approval',
  'inspection-reporting',
  'tbm',
  'work-permit',
  'equipment-dispatch',
  'safety-training',

  'shared-drive',
]);

const featureById = (id: string): Feature => {
  const feature = features.find((candidate) => candidate.id === id);
  if (!feature) throw new Error(`Unknown workspace feature: ${id}`);
  return feature;
};

const enterpriseMasterFeatureIds = new Set<string>([
  'site-settings',
  'my-info',
  'user-approval',

]);

const canAccessFeature = (role: Role, feature: Feature) =>
  (role === 'system' ? enterpriseMasterFeatureIds.has(feature.id) :
  (feature.capabilities.some((item) => roleProfiles[role].capabilities.includes(item)) &&
    (!feature.roles || feature.roles.includes(role))));

const visibleFeatures = (role: Role) => {
  return features.filter(
    (feature) =>
      canAccessFeature(role, feature) &&
      feature.navigation !== false &&
      (role !== 'partner' || partnerNavigationFeatureIds.has(feature.id)),
  );
};

/** Personal display preference. It is not a work action, so it lives at the
 * foot of the left rail, and at the foot of the mobile menu where there is no
 * rail. */
function ThemeToggle({ dark, setDark }: { dark: boolean; setDark: (value: boolean) => void }) {
  return (
    <button
      type="button"
      className="rail-theme"
      aria-label="다크 모드 전환"
      aria-pressed={dark}
      onClick={() => setDark(!dark)}
    >
      {dark ? <Sun size={20} /> : <Moon size={20} />}
      <span>{dark ? '라이트 모드' : '다크 모드'}</span>
    </button>
  );
}

function FavoritesStrip({
  favorites,
  openFeature,
  openCommand,
}: {
  favorites: readonly Feature[];
  openFeature: (feature: Feature) => void;
  openCommand: () => void;
}) {
  return (
    <section className="priority-strip favorites-strip" aria-label="즐겨찾기">
      <div>
        <span>즐겨찾기</span>
        <strong>자주 쓰는 업무</strong>
      </div>
      <nav>
        {favorites.length ? (
          favorites.map((feature) => (
            <button
              key={feature.id}
              onClick={() => openFeature(feature)}
              onPointerEnter={() => prefetchFeatureRoute(feature.id)}
              onFocus={() => prefetchFeatureRoute(feature.id)}
            >
              {feature.label}
            </button>
          ))
        ) : (
          <button className="favorites-add" onClick={openCommand}>
            <Plus size={15} /> 업무 추가
          </button>
        )}
      </nav>
    </section>
  );
}

function RoleHome({
  role,
  workHomeSnapshot,
  openFeature,
  favorites,
  openCommand,
  boardAuthority,
  boardOwner,
  homeScope,
  homeFeatureIds,
  onInspection,
}: {
  role: Role;
  workHomeSnapshot: WorkHomeSnapshot;
  openFeature: (feature: Feature, readSiteId?: string) => void;
  favorites: readonly Feature[];
  openCommand: () => void;
  boardAuthority: SafetyBoardAuthority;
  boardOwner: { uid: string; contractorId: string | null };
  homeScope: AccessScope['resource'];
  homeFeatureIds: readonly string[];
  onInspection: (period: {from:string;to:string} | null) => void;
}) {
  if (homeScope.kind === 'tenant' && ['hqAudit', 'hqSafety'].includes(role)) {
    return <HeadquartersDashboard key={`${boardOwner.uid}:${JSON.stringify(homeScope)}`} uid={boardOwner.uid} siteId={null} featureIds={homeFeatureIds} onOpen={(id, readSiteId)=>openFeature(featureById(id), readSiteId)}/>;
  }
  if (homeScope.kind === 'tenant') return <HubEmpty>소속 사업장과 이용 권한을 확인해 주세요.</HubEmpty>;
  return (
    <OperationsDashboard key={`${boardOwner.uid}:${JSON.stringify(homeScope)}`} uid={boardOwner.uid} scope={homeScope} featureIds={homeFeatureIds} onOpen={id=>openFeature(featureById(id))} onInspection={onInspection} authority={boardAuthority} owner={boardOwner} snapshot={workHomeSnapshot} isHq={role==='hqAudit'||role==='hqSafety'}>
      <FavoritesStrip favorites={favorites} openFeature={openFeature} openCommand={openCommand} />
    </OperationsDashboard>
  );
}

function FeaturePage({
  feature,
  role,
  close,
}: {
  feature: Feature;
  role: Role;
  close: () => void;
}) {
  const isFinding = feature.id === 'finding-action';
  const statusFilters = [
    { value: 'open', label: '진행 중' },
    { value: 'review', label: '검토 대기' },
    { value: 'closed', label: '완료·종결' },
  ] as const;
  const [status, setStatus] = useState<(typeof statusFilters)[number]['value']>('open');
  return (
    <section className="feature-page">
      <HubHeader
        title={feature.label}
        onBack={close}
        pill={
          role !== 'hqAudit' &&
          !['inspection-reporting',  ].includes(feature.id) ? (
            <Button icon={<Plus size={18} />}>
              {isFinding && role === 'client'
                ? '지적 발행'
                : isFinding && role === 'partner'
                  ? '조치 제출'
                  : '새 문서 작성'}
            </Button>
          ) : undefined
        }
      />
      <HubFilters
        items={statusFilters}
        value={status}
        onChange={setStatus}
        ariaLabel={`${feature.label} 상태 필터`}
      />
      <div className="feature-workspace">
        <section>
          <HubEmpty>
            진행 중인 문서가 없습니다. 상태 필터를 바꾸거나 새 문서를 작성해 주세요.
          </HubEmpty>
        </section>
      </div>
    </section>
  );
}

function Improvements({
  identity,
  scope,
  canTriage,
}: {
  identity: { id: string; name: string };
  scope: AccessScope['resource'];
  canTriage: boolean;
}) {
  return (
    <Suspense
      fallback={
        <FeatureSkeleton
          screenClassName="feature-page feedback-workspace-page"
          masthead="feature-masthead feedback-masthead"
          label="시스템 개선 관리 불러오는 중"
          panels={3}
        />
      }
    >
      <FeedbackWorkspace key={`${identity.id}:${operationalSelectedScopeId(scope)}`} identity={identity} scope={scope} canTriage={canTriage} />
    </Suspense>
  );
}

function MobileHome({
  onAccount,
  displayName,
  scopeLabel,
  contractorView,
  inspectionContent,
  openChecklist,
  offline,
  availableFeatureIds,
  openReport,
  openFeature,
  openQuickInspection,
  openFindings,
}: {
  onAccount(): void;
  displayName: string;
  scopeLabel: string;
  contractorView: boolean;
  inspectionContent: ReactNode;
  openChecklist(kind: FieldChecklistKind): void;
  offline: boolean;
  availableFeatureIds: ReadonlySet<string>;
  openReport(): void;
  openFeature: (feature: Feature) => void;
  openQuickInspection(): void;
  openFindings(): void;
}) {
  return (
    <FieldMobileShell
      onAccount={onAccount}
      displayName={displayName}
      scopeLabel={scopeLabel}
      contractorView={contractorView}
      inspectionContent={inspectionContent}
      offline={offline}
      entitlements={{
        tbm: availableFeatureIds.has('tbm'),
        inspection: availableFeatureIds.has('inspection-reporting'),
        findings: availableFeatureIds.has('finding-action'),
        report: true,
      }}
      actions={{
        openTbm: () => openFeature(featureById('tbm')),
        openInspection: () => openFeature(featureById('inspection-reporting')),
        openChecklist,
        openQuickInspection,
        openFindings,
        openReport,
      }}
    />
  );
}

export function AuthenticatedWorkspace({
  session,
  auth,
}: {
  session: AuthenticatedSession;
  auth: AuthAdapter;
}) {
  const { state: pwaState, install: installPwa } = usePwa();
  const activeScope =
    session.scopes.find((scope) => scope.id === session.activeScopeId) ?? session.scopes[0]!;
  const role = activeScope.role;
  const [compactViewport, setCompactViewport] = useState(
    () => window.matchMedia(mobileSessionMediaQuery).matches,
  );
  const mobile = compactViewport;
  const governancePublicSiteId =
    activeScope.resource.kind === 'tenant' ? '' : activeScope.resource.siteId;
  const approvalAuthorized = canOpenApprovalHub(role, activeScope.capabilities, activeScope.featureAccess, activeScope.developerReadOnly);
  const configuredApprovalAuthority = getApprovalAuthority();
  const approvalPorts = approvalAuthorized ? configuredApprovalAuthority : null;
  const approvalScope = useMemo(
    () =>
      activeScope.resource.kind === 'tenant'
        ? activeScope.resource
        : {
            ...activeScope.resource,
            siteId:
              activeScope.resource.siteId === 'eumseong'
                ? 'site-eumseong'
                : activeScope.resource.siteId,
          },
    [activeScope.resource],
  );
  const [feedbackDeveloperIdentity, setFeedbackDeveloperIdentity] = useState<string | null>(null);
  useEffect(() => {
    let current = true;
    setFeedbackDeveloperIdentity(null);
    void import('./features/feedback/repository').then(module => module.loadFeedbackDeveloperAccess(session.identity.id))
      .then(allowed => { if (current) setFeedbackDeveloperIdentity(allowed ? session.identity.id : null); })
      .catch(() => { if (current) setFeedbackDeveloperIdentity(null); });
    return () => { current = false; };
  }, [session.identity.id]);
  const isSystemFeedbackAdmin = canManageSystemFeedback(activeScope, feedbackDeveloperIdentity === session.identity.id);
  const featureAccessState = useMemo(() => featureAccessProjectionStatus(activeScope.featureAccess), [activeScope.featureAccess]);
  const enforcedIncidentAuthor = featureAccessState.kind === 'enforced'
    ? !activeScope.developerReadOnly && activeScope.resource.kind !== 'tenant' && featureAccessState.features.has('incident')
    : activeScope.capabilities.includes('victim-registry:author');
  const enforcedRiskAuthor = featureAccessState.kind === 'enforced'
    ? !activeScope.developerReadOnly && featureAccessState.features.has('regular-risk')
    : activeScope.capabilities.includes('document:write');
  const canUseSystemManagement = canDiscoverWorkspaceFeature('system-management', activeScope, feedbackDeveloperIdentity === session.identity.id);
  const systemManagementRepository = useMemo(() => createSystemManagementRepository(), []);
  const contractorManagementAuthorized = canManageContractorDirectory(activeScope);
  const contractorManagementVisible = canViewContractorDirectory(activeScope);
  const equipmentDispatchActorProjection = equipmentDispatchProjection(activeScope, session.activeScopeId);
  const identityNotificationAuthorized =
    activeScope.persona === 'safety-manager' &&
    activeScope.resource.kind === 'site';
  const identityNotificationSiteId = activeScope.resource.kind === 'site' ? activeScope.resource.siteId : '';
  const identityNotificationScopeKey = JSON.stringify([session.identity.id, identityNotificationSiteId, activeScope.id]);
  const identityNotificationScopeRef = useRef({ scope: identityNotificationScopeKey });
  if (identityNotificationScopeRef.current.scope !== identityNotificationScopeKey)
    identityNotificationScopeRef.current = { scope: identityNotificationScopeKey };
  const inspectionPersistenceContext = useMemo(
    () => ({
      tenantId: activeScope.resource.tenantId,
      siteId: activeScope.resource.kind === 'tenant' ? 'all-sites' : activeScope.resource.siteId,
      userId: session.identity.id,
      scope: activeScope.resource,
    }),
    [activeScope.resource, session.identity.id],
  );
  const tbmPersistenceContext = useMemo(
    () => ({
      tenantId: activeScope.resource.tenantId,
      siteId: activeScope.resource.kind === 'tenant' ? 'eumseong' : activeScope.resource.siteId,
      contractorId:
        activeScope.resource.kind === 'contractor'
          ? activeScope.resource.contractorId
          : 'all-contractors',
    }),
    [activeScope.resource],
  );
  /* Lifted here, above the screen switch, rather than called inside the home screen components:
     the left navigation needs these counts on every screen, not only while `screen === 'home'`.
     `useWorkHomeProjection` still makes exactly one call per mount -- mounting it once at the
     workspace root instead of inside RoleHome means it survives navigating away from home, so
     this is not an additional fetch beyond what the home badge already made.
     The read itself was started before this component existed, back when the server confirmed
     the membership; the hook picks up that answer rather than making its own call. */
  // A factory being viewed by HQ is not the user's personal assignment inbox.
  // Its dashboard counts come from the selected factory's operational summary.
  const workHomeScopeId = workspaceHomeSelectedScopeId(activeScope.resource);
  const workHomeSnapshot = useWorkHomeProjection(workHomeScopeId, !(isHeadquartersScope(activeScope) && activeScope.resource.kind === 'site'));
  const pendingApprovals = pendingApprovalCountOf(workHomeSnapshot);
  const unresolvedFindings = unresolvedFindingCountOf(workHomeSnapshot);
  const dueApprovals = dueActionCountOf(workHomeSnapshot, 'approval');
  const dueFindings = dueActionCountOf(workHomeSnapshot, 'finding');
  /* electronic-approval owns every approval request (approval-hub/model.ts); inspection-reporting
     is the only menu entry that reaches unresolved findings -- the individual finding screens
     (inspection-unresolved, finding-action) are internal views of
     InspectionReadWorkspace / InspectionWorkflow, opened from inside it, and carry
     `navigation: false` so they never appear in the menu on their own. */
  const navigationBadgesByFeatureId = useMemo(() => {
    const badges = new Map<string, NavigationBadge>();
    const approvalBadge = navigationBadgeOf('전자결재', '대기', pendingApprovals, dueApprovals);
    if (approvalBadge) badges.set('electronic-approval', approvalBadge);
    const findingBadge = navigationBadgeOf('점검', '미조치', unresolvedFindings, dueFindings);
    if (findingBadge) badges.set('inspection-reporting', findingBadge);
    return badges;
  }, [dueApprovals, dueFindings, pendingApprovals, unresolvedFindings]);
  const navigationFeature = useCallback(
    (id: string) => {
      if (mobile && !isFieldMobileFeature(id)) return null;
      const feature = features.find(candidate => candidate.id === id);
      if (!feature) return null;
      if (!canDiscoverWorkspaceFeature(id, activeScope, isSystemFeedbackAdmin)) return null;
      if (featureAccessState.kind === 'enforced' || featureAccessState.kind === 'malformed')
        return projectWorkspaceNavigation([feature], { role, persona: activeScope.persona,
          capabilities: activeScope.capabilities, resource: activeScope.resource,
          featureAccess: activeScope.featureAccess })[0] ?? null;
      if (id === 'product-feedback') return isSystemFeedbackAdmin ? feature : null;
      if (id === 'system-management') return isSystemFeedbackAdmin ? feature : null;
      if (id === 'tbm-settings' && activeScope.resource.kind === 'site' &&
          activeScope.capabilities.includes('site:manage')) return feature;
      if (id === 'contractor-management') return contractorManagementVisible ? feature : null;
      if (id === 'equipment-dispatch') return equipmentDispatchActorProjection ? feature : null;
      if (!canAccessFeature(role, feature)) return null;
      return (
        projectWorkspaceNavigation([feature], {
          role,
          persona: activeScope.persona,
          capabilities: activeScope.capabilities,
          resource: activeScope.resource,
        })[0] ?? null
      );
    },
    [
      mobile,
      activeScope,
      isSystemFeedbackAdmin,
      featureAccessState.kind,
      contractorManagementVisible,
      equipmentDispatchActorProjection,
      role,
    ],
  );
  const routedFeature = useCallback(() => {
    const pathname = normalizeWorkspacePath(window.location.pathname);
    const routeIds: Readonly<Record<string, string>> = {
      '/approvals': 'electronic-approval', '/inspections': 'inspection-reporting',
      '/inspections/read': 'inspection-reporting', '/inspections/unresolved': 'inspection-unresolved',
      '/tbm': 'tbm', '/incidents/victims': 'incident', '/education': 'safety-training',
      '/safety-content': 'safety-content', '/work': 'work-permit', '/risk-assessment': 'regular-risk',
      '/budget': 'safety-cost', '/assets/msds': 'chemical-msds-management', '/documents': 'shared-drive',
      '/heat-index': 'heat-index', '/equipment-dispatch': 'equipment-dispatch',
      '/communications/meetings': 'meeting-minutes',
    };
    if (pathname === '/preview/meetings' && import.meta.env.DEV &&
        ['true', '1'].includes(import.meta.env.VITE_LOCAL_PREVIEW ?? '') &&
        ['127.0.0.1', 'localhost', '::1'].includes(window.location.hostname))
      return navigationFeature('meeting-minutes');
    if (pathname === '/developer/feedback') return navigationFeature('product-feedback');
    const id = routeIds[pathname];
    return id ? navigationFeature(id) : null;
  }, [navigationFeature]);
  const initialRouteFeature = routedFeature();
  const [screen, setScreen] = useState<Screen>(initialRouteFeature ? 'feature' : 'home');
  const [activeFeature, setActiveFeature] = useState<Feature>(
    initialRouteFeature ?? featureById('finding-action'),
  );
  useEffect(() => {
    if (window.location.pathname !== '/developer/feedback') return;
    if (isSystemFeedbackAdmin) {
      setActiveFeature(featureById('product-feedback'));
      setScreen('feature');
    } else setScreen('home');
  }, [isSystemFeedbackAdmin]);
  const [inspectionEntryPreset, setInspectionEntryPreset] = useState<InspectionStartPreset | null>(
    null,
  );
  const [findingCenterPreset, setFindingCenterPreset] = useState<FindingCenterPreset | null>(null);
  const [homeInspectionPeriod, setHomeInspectionPeriod] = useState<{ from: string; to: string } | null>(null);
  const [headquartersReadContext, setHeadquartersReadContext] = useState<HeadquartersReadContext | null>(null);
  const [approvalRequestToOpen, setApprovalRequestToOpen] = useState<string | null>(null);
  const [incidentRiskSource, setIncidentRiskSource] = useState<IncidentRiskSource | undefined>();
  const [incidentAssessmentId, setIncidentAssessmentId] = useState<string | undefined>();
  const incidentRiskScopeKey = JSON.stringify(activeScope.resource);
  useEffect(() => {
    setIncidentRiskSource(undefined);
    setIncidentAssessmentId(undefined);
  }, [session.identity.id, incidentRiskScopeKey]);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [navigationPinned, setNavigationPinned] = useState(
    () => localStorage.getItem('kop-navigation-expanded') === 'true',
  );
  const [command, setCommand] = useState(false);
  const [query, setQuery] = useState('');
  const [dark, setDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [notice, setNotice] = useState(false);
  const [identityUnreadCount, setIdentityUnreadCount] = useState(0);
  const [identityNotificationError, setIdentityNotificationError] = useState('');
  const [identityNotifications, setIdentityNotifications] = useState<
    readonly {
      notificationId: string;
      requestId: string;
      displayNameSnapshot: string;
      createdAt: string;
      deepLink: '/admin/users';
      unread: boolean;
    }[]
  >([]);
  const [accountMenu, setAccountMenu] = useState(false);
  const [siteAdminSectionId, setSiteAdminSectionId] = useState<string | null>(() =>
    siteAdministrationSectionForPath(window.location.pathname),
  );
  const [visitedSiteAdminSectionIds, setVisitedSiteAdminSectionIds] = useState<readonly string[]>(
    () => {
      const initial = siteAdministrationSectionForPath(window.location.pathname);
      return initial ? [initial] : [];
    },
  );
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackRoute, setFeedbackRoute] = useState('/');
  const [feedbackInitialView, setFeedbackInitialView] = useState<'report' | 'mine'>('report');
  const identityNotificationPort = useCallback(async () => {
    if (!identityNotificationAuthorized) return null;
    const config = {
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
      appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
      appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
    };
    if (Object.values(config).some((value) => !value.trim())) return null;
    const { createIdentityAccessNotificationPort } =
      await import('@kop/operations-firebase-adapter');
    return createIdentityAccessNotificationPort(config, identityNotificationSiteId);
  }, [identityNotificationAuthorized, identityNotificationSiteId]);
  const refreshIdentityNotificationCount = useCallback(async () => {
    const scopeKey = identityNotificationScopeRef.current;
    if (scopeKey.scope !== identityNotificationScopeKey) return;
    try {
      const port = await identityNotificationPort();
      if (!port) return;
      const value = (await port.count()) as { unreadCount?: unknown };
      if (identityNotificationScopeRef.current === scopeKey &&
        Number.isSafeInteger(value.unreadCount) && Number(value.unreadCount) >= 0) {
        setIdentityUnreadCount(Number(value.unreadCount));
        setIdentityNotificationError('');
      }
    } catch {
      if (identityNotificationScopeRef.current === scopeKey) {
        setIdentityUnreadCount(0);
        setIdentityNotificationError('알림을 확인하지 못했습니다. 잠시 후 다시 열어 주세요.');
      }
    }
  }, [identityNotificationPort, identityNotificationScopeKey]);
  useEffect(() => {
    const clear = () => {
      identityNotificationScopeRef.current = { scope: identityNotificationScopeRef.current.scope };
      setIdentityUnreadCount(0);
      setIdentityNotifications([]);
      setIdentityNotificationError('');
      setNotice(false);
    };
    window.addEventListener('kop:sensitive-memory-cache-clear', clear);
    return () => window.removeEventListener('kop:sensitive-memory-cache-clear', clear);
  }, []);
  useEffect(() => {
    setIdentityUnreadCount(0);
    setIdentityNotifications([]);
    setIdentityNotificationError('');
    setNotice(false);
    if (!identityNotificationAuthorized) {
      return;
    }
    const idleWindow = window as Window & {
      requestIdleCallback?: (work: () => void) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    const schedule =
      idleWindow.requestIdleCallback?.bind(idleWindow) ??
      ((work: () => void) => window.setTimeout(work, 500));
    const handle = schedule(() => void refreshIdentityNotificationCount());
    return () => {
      idleWindow.cancelIdleCallback?.(handle);
      window.clearTimeout(handle);
    };
  }, [identityNotificationAuthorized, refreshIdentityNotificationCount]);
  const [routePending, startRouteTransition] = useTransition();
  useEffect(() => {
    if (!accountMenu) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('.account-popover, .top-actions > .profile'))
        return;
      setAccountMenu(false);
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer);
  }, [accountMenu]);
  const revealWorkspace=useWorkspaceReady();
  const emptyTenantHome = activeScope.resource.kind === 'tenant' && !['hqAudit', 'hqSafety'].includes(role);
  useEffect(() => {
    if (emptyTenantHome || ((mobile || screen !== 'home') && workHomeSnapshot.state !== 'loading'))
      revealWorkspace();
  }, [emptyTenantHome, mobile, screen, workHomeSnapshot.state, revealWorkspace]);
  /* The five sections behind the gear, on the terms the features table already grants them.
     They carry `navigation: false`, so they are absent from the menu - but they are still work a
     person searches for by name, so the same projected list also feeds the Ctrl-K index below. */
  const siteAdministrationFeatures = useMemo(
    () =>
      siteAdministrationSectionIds.flatMap((id) => {
        const feature = navigationFeature(id);
        return feature ? [feature] : [];
      }),
    [navigationFeature],
  );
  const siteAdministrationSections = useMemo<readonly SiteAdministrationSection[]>(
    () =>
      siteAdministrationFeatures.map((feature) => ({
        id: feature.id,
        label: feature.label,
        group: [
          'site-settings',
          'organization-chart',
          'work-location-registry',
          'tbm-settings',
          'contractor-management',
        ].includes(feature.id)
          ? '사업장 설정'
          : feature.id === 'inspection-checklists'
            ? '점검 설정'
            : ['my-info', 'user-approval'].includes(feature.id)
              ? '사용자 설정'
              : '운영 지원',
      })),
    [siteAdministrationFeatures],
  );
  const entitledSiteAdminSectionId =
    !mobile && siteAdminSectionId &&
    siteAdministrationSections.some((section) => section.id === siteAdminSectionId)
      ? siteAdminSectionId
      : null;
  useEffect(() => {
    if (!siteAdminSectionId || entitledSiteAdminSectionId) return;
    if (siteAdministrationSectionForPath(window.location.pathname))
      window.history.replaceState({}, '', '/');
    setSiteAdminSectionId(null);
  }, [entitledSiteAdminSectionId, siteAdminSectionId]);
  const siteAdministrationOpen = siteAdminSectionId !== null;
  const openSiteAdministration = (sectionId: string) => {
    if (mobile) return;
    prefetchFeatureRoute(sectionId);
    setVisitedSiteAdminSectionIds((current) =>
      current.at(-1) === sectionId
        ? current
        : [...current.filter((id) => id !== sectionId).slice(-2), sectionId],
    );
    const path = siteAdministrationPaths[sectionId];
    if (path && window.location.pathname !== path) window.history.pushState({}, '', path);
    startRouteTransition(() => setSiteAdminSectionId(sectionId));
    setAccountMenu(false);
    setNavigationOpen(false);
    setFeedbackOpen(false);
  };
  const closeSiteAdministration = useCallback(() => {
    if (siteAdministrationSectionForPath(window.location.pathname))
      window.history.pushState({}, '', '/');
    setSiteAdminSectionId(null);
  }, []);
  useEffect(() => {
    if (!siteAdministrationOpen) return;
    const preferredOrder = [
      'site-settings',
      'organization-chart',
      'work-location-registry',
      'inspection-checklists',
      'tbm-settings',
      'user-approval',
      'contractor-management',
    ];
    const available = preferredOrder.filter((id) =>
      siteAdministrationSections.some((section) => section.id === id),
    );
    const timers = available.map((sectionId, index) =>
      window.setTimeout(
        () => {
          prefetchFeatureRoute(sectionId);
        },
        250 + index * 320,
      ),
    );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [siteAdministrationOpen, siteAdministrationSections]);
  const closeFeedback = useCallback(() => setFeedbackOpen(false), []);
  const openFeedback = () => {
    setFeedbackRoute(window.location.pathname);
    setFeedbackInitialView('report');
    setFeedbackOpen(true);
    setAccountMenu(false);
  };
  const roleFeatures = useMemo(
    () =>
      projectWorkspaceNavigation(
        (featureAccessState.kind === 'enforced' || featureAccessState.kind === 'malformed'
          ? features.filter(feature => feature.navigation !== false) : visibleFeatures(role)).filter(
          (feature) =>
            (!mobile || isFieldMobileFeature(feature.id)) &&
            canDiscoverWorkspaceFeature(feature.id, activeScope, isSystemFeedbackAdmin) &&
            (featureAccessState.kind === 'enforced' || feature.id !== 'equipment-dispatch' || Boolean(equipmentDispatchActorProjection)),
        ),
        {
          role,
          persona: activeScope.persona,
          capabilities: activeScope.capabilities,
          resource: activeScope.resource,
          featureAccess: activeScope.featureAccess,
        },
      ),
    [
      mobile,
      activeScope,
      isSystemFeedbackAdmin,
      featureAccessState.kind,
      equipmentDispatchActorProjection,
      role,
    ],
  );
  const mobileFieldFeatureIds = useMemo(
    () =>
      new Set(
        ['tbm', 'inspection-reporting', 'finding-action'].filter((id) =>
          Boolean(navigationFeature(id)),
        ),
      ),
    [navigationFeature],
  );
  const reviewedRailFeatures = useMemo(
    () =>
      reviewedRailFeatureOrder.flatMap((id) => {
        const feature = roleFeatures.find((candidate) => candidate.id === id);
        return feature ? [feature] : [];
      }),
    [roleFeatures],
  );
  const reviewPendingRailFeatures = useMemo(
    () =>
      roleFeatures.filter(
        (feature) =>
          !topUtilityFeatureIds.has(feature.id) &&
          !reviewedRailFeatureOrder.some((id) => id === feature.id) &&
          !bottomRailFeatureOrder.some((id) => id === feature.id),
      ),
    [roleFeatures],
  );
  const bottomRailFeatures = useMemo(
    () =>
      bottomRailFeatureOrder.flatMap((id) => {
        const feature = roleFeatures.find((candidate) => candidate.id === id);
        return feature ? [feature] : [];
      }),
    [roleFeatures],
  );
  const topUtilityFeatures = useMemo(
    () =>
      ['meeting-minutes', 'electronic-approval', 'shared-drive'].flatMap((id) => {
        const feature = roleFeatures.find((candidate) => candidate.id === id);
        return feature ? [feature] : [];
      }),
    [roleFeatures],
  );
  const directRailFeatures = useMemo(
    () => [...reviewedRailFeatures, ...reviewPendingRailFeatures, ...bottomRailFeatures],
    [bottomRailFeatures, reviewPendingRailFeatures, reviewedRailFeatures],
  );
  useEffect(() => {
    const idleWindow = window as Window & {
      requestIdleCallback?: (work: () => void, options?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    let idleHandle = 0;
    const timer = window.setTimeout(() => {
      const work = () =>
        prefetchEntitledRoutes([
          ...roleFeatures.slice(0, 5).map((feature) => feature.id),
          ...siteAdministrationFeatures.map((feature) => feature.id),
        ]);
      if (idleWindow.requestIdleCallback) {
        idleHandle = idleWindow.requestIdleCallback(work, { timeout: 3000 });
      } else {
        work();
      }
    }, 1200);
    return () => {
      window.clearTimeout(timer);
      if (idleHandle) idleWindow.cancelIdleCallback?.(idleHandle);
    };
  }, [roleFeatures, siteAdministrationFeatures]);
  /* Publisher entitlement, restated from the session the identity server issued -- persona,
     capabilities and scope all arrive on it. The board wave re-checks every command, so a
     mismatch here can only hide a control, never grant one. */
  const boardAuthority = useMemo<SafetyBoardAuthority>(
    () =>
      safetyBoardAuthority({
        persona: activeScope.persona,
        capabilities: activeScope.capabilities,
        resource: activeScope.resource,
        featureAccess: activeScope.featureAccess,
        developerReadOnly: activeScope.developerReadOnly,
      }),
    [activeScope.capabilities, activeScope.developerReadOnly, activeScope.featureAccess, activeScope.persona, activeScope.resource],
  );
  /* Search covers the menu plus the gear panel's five sections. A gear section is not a menu
     entry, so it carries no favourite control: the home favourites strip only holds features the
     navigation projects. Choosing one still opens the gear panel, because openFeature routes it
     there. */
  const searchIndex = useMemo(
    () => [
      ...roleFeatures.map((feature) => {
        const category = groups.find((group) => group.id === feature.group)?.label ?? '업무';
        const aliases = feature.id.split('-');
        return {
          feature,
          category,
          favoritable: true,
          description: `${category} 메뉴`,
          searchable: [feature.label, category, feature.id, ...aliases].join(' ').toLowerCase(),
        };
      }),
      ...siteAdministrationFeatures.map((feature) => {
        const category = '사업장 관리';
        const aliases = feature.id.split('-');
        return {
          feature,
          category,
          favoritable: false,
          description: category,
          searchable: [feature.label, category, feature.id, ...aliases].join(' ').toLowerCase(),
        };
      }),
    ],
    [roleFeatures, siteAdministrationFeatures],
  );
  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return normalized
      ? searchIndex.filter((entry) => entry.searchable.includes(normalized))
      : searchIndex;
  }, [query, searchIndex]);
  const favoritePreferenceKey = `kop-home-favorites:${session.identity.id}:${activeScope.id}`;
  const [savedFavoriteIds, setSavedFavoriteIds] = useState<readonly string[] | null>(null);
  useEffect(() => {
    setSavedFavoriteIds(favoritePreferences.load(favoritePreferenceKey));
  }, [favoritePreferenceKey]);
  const favoriteIds = useMemo(
    () =>
      projectFavoriteIds(
        savedFavoriteIds,
        roleFeatures.map((feature) => feature.id),
        roleFeatures.slice(0, 5).map((feature) => feature.id),
      ),
    [roleFeatures, savedFavoriteIds],
  );
  const favoriteFeatures = favoriteIds.flatMap((id) => {
    const feature = roleFeatures.find((candidate) => candidate.id === id);
    return feature ? [feature] : [];
  });
  const saveFavorites = (ids: readonly string[]) => {
    setSavedFavoriteIds(ids);
    favoritePreferences.save(favoritePreferenceKey, ids);
  };
  const toggleFavorite = (id: string) =>
    saveFavorites(
      favoriteIds.includes(id)
        ? favoriteIds.filter((candidate) => candidate !== id)
        : [...favoriteIds, id],
    );
  const reorderFavorite = (id: string, offset: -1 | 1) =>
    saveFavorites(moveFavorite(favoriteIds, id, offset));

  const openFeature = (feature: Feature, readSiteId?: string) => {
    setHeadquartersReadContext(['shared-drive', 'work-permit'].includes(feature.id) && activeScope.resource.kind === 'tenant' && ['hqAudit', 'hqSafety'].includes(role) && readSiteId
      ? createHeadquartersReadContext(session.identity.id, readSiteId) : null);
    setIncidentRiskSource(undefined);
    setIncidentAssessmentId(undefined);
    setHomeInspectionPeriod(null);
    const projectedFeature = navigationFeature(feature.id);
    if (!projectedFeature) return;
    if (mobile && ['inspection-reporting', 'inspection-unresolved', 'finding-action'].includes(feature.id)) {
      setScreen('home');
      setNavigationOpen(false);
      window.history.replaceState({}, '', '/field/inspection');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }
    if (feature.id === 'product-feedback' && !isSystemFeedbackAdmin) return;
    if (siteAdministrationSectionIds.some((id) => id === feature.id)) {
      openSiteAdministration(feature.id);
      return;
    }
    prefetchFeatureRoute(feature.id);
    startRouteTransition(() => {
      setActiveFeature(projectedFeature);
      setScreen('feature');
    });
    if (feature.id === 'meeting-minutes') window.history.pushState({}, '', '/communications/meetings');
    if (feature.id === 'tbm') window.history.pushState({}, '', '/tbm');
    if (feature.id === 'incident') window.history.pushState({}, '', '/incidents/victims');
    if (feature.id === 'electronic-approval') {
      setApprovalRequestToOpen(null);
      window.history.pushState({}, '', '/approvals');
    }
    if (feature.id === 'safety-training') window.history.pushState({}, '', '/education');
    if (feature.id === 'safety-content') window.history.pushState({}, '', '/safety-content');
    if (feature.id === 'regular-risk') window.history.pushState({}, '', '/risk-assessment');

    if (feature.id === 'safety-cost') window.history.pushState({}, '', '/budget');

    if (feature.id === 'inspection-reporting') window.history.pushState({}, '', '/inspections');
    if (feature.id === 'inspection-unresolved')
      window.history.pushState({}, '', '/inspections/unresolved');

    if (feature.id === 'product-feedback') window.history.pushState({}, '', '/developer/feedback');

    if (feature.id === 'chemical-msds-management') window.history.pushState({}, '', '/assets/msds');
    if (feature.id === 'heat-index') window.history.pushState({}, '', '/heat-index');
    if (feature.id === 'work-permit') window.history.pushState({}, '', '/work');
    if (feature.id === 'equipment-dispatch')
      window.history.pushState({}, '', '/equipment-dispatch');

    if (feature.id === 'shared-drive') window.history.pushState({}, '', '/documents');

    if (feature.id === 'meeting-minutes')
      window.history.pushState({}, '', '/communications/meetings');

    setNavigationOpen(false);
  };
  const openFindingCenter = (preset?: InspectionFindingPreset) => {
    setFindingCenterPreset(preset ?? null);
    prefetchFeatureRoute('finding-action');
    startRouteTransition(() => {
      setActiveFeature(featureById('finding-action'));
      setScreen('feature');
    });
    window.history.pushState({}, '', '/inspections');
    setNavigationOpen(false);
  };
  const openInspectionDashboard = () => {
    setHomeInspectionPeriod(null);
    setFindingCenterPreset(null);
    startRouteTransition(() => {
      setActiveFeature(featureById('inspection-reporting'));
      setScreen('feature');
    });
    window.history.pushState({}, '', '/inspections');
  };
  const openQuickInspection = () => {
    if (!mobileFieldFeatureIds.has('inspection-reporting')) return;
    setInspectionEntryPreset({ category: 'quick' });
    setScreen('inspection');
    setNavigationOpen(false);
  };
  const openMobileChecklist = (kind: FieldChecklistKind) => {
    if (!mobileFieldFeatureIds.has('inspection-reporting')) return;
    setInspectionEntryPreset(kind === '특별점검' ? { category: 'special' } : { category: 'structured', structuredKind: kind });
    setScreen('inspection');
    setNavigationOpen(false);
  };
  const mobileInspectionTasks = mobileFieldFeatureIds.has('finding-action') ? (
    <Suspense fallback={<FeatureLoading label="미조치 건 불러오는 중" />}>
      <InspectionWorkflow role={role} identity={{ id: session.identity.id, name: session.identity.displayName }}
        persistenceContext={inspectionPersistenceContext} view="findings" mobileTaskList
        findingPreset={{ status: 'unresolved' }} onBack={() => setScreen('home')} />
    </Suspense>
  ) : null;
  const closeFeature = () => {
    setInspectionEntryPreset(null);
    setFindingCenterPreset(null);
    if (window.location.pathname !== '/') window.history.pushState({}, '', '/');
    setScreen('home');
  };
  const selectScope = async (scopeId: string) => {
    await switchWorkspaceScope(session, scopeId, {
      resetLocation: () => {
        const previous = window.location.pathname + window.location.search + window.location.hash;
        window.history.replaceState({}, '', '/');
        return () => window.history.replaceState({}, '', previous);
      },
      clearCaches: clearSensitiveMemoryCaches,
      selectScope: (id) => auth.selectScope(id),
    });
    setScreen('home');
    setQuery('');
    setNavigationOpen(false);
  };
  const togglePersistentNavigation = () => {
    if (mobile) {
      setNavigationOpen((current) => !current);
      return;
    }
    const next = !navigationPinned;
    setNavigationPinned(next);
    localStorage.setItem('kop-navigation-expanded', String(next));
    setNavigationOpen(false);
  };
  useEffect(() => {
    const viewport = window.matchMedia(mobileSessionMediaQuery);
    const normalizedPath = normalizeWorkspacePath(window.location.pathname);
    if (normalizedPath !== window.location.pathname) {
      window.history.replaceState({}, '', normalizedPath);
      setSiteAdminSectionId(null);
      const redirectedFeature = routedFeature();
      if (redirectedFeature) {
        setActiveFeature(redirectedFeature);
        setScreen('feature');
      } else setScreen('home');
    }

    if (window.location.pathname.startsWith('/contractor-compliance') && !routedFeature())
      window.history.replaceState({}, '', '/');
    if (
      (window.location.pathname === '/documents' ||
        window.location.pathname.startsWith('/documents/swp') ||
        window.location.pathname.startsWith('/documents/manuals') ||
        window.location.pathname.startsWith('/assets/') ||
        [
          '/approvals',
          '/records/statutory',
          '/shared-resources',
          '/organisation',
          '/site-map',
          '/safety-design',
        ].includes(window.location.pathname)) &&
      !routedFeature()
    ) {
      window.history.replaceState({}, '', '/');
      setScreen('home');
    }
    const syncViewport = () => setCompactViewport(viewport.matches);
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommand(true);
      }
      if (event.key === 'Escape') {
        setCommand(false);
        setNavigationOpen(false);
        setAccountMenu(false);
      }
    };
    viewport.addEventListener('change', syncViewport);
    const onPopState = () => {
      const normalizedPath = normalizeWorkspacePath(window.location.pathname);
      if (normalizedPath !== window.location.pathname)
        window.history.replaceState({}, '', normalizedPath);
      const sectionId = siteAdministrationSectionForPath(window.location.pathname);
      setSiteAdminSectionId(sectionId);
      if (sectionId)
        setVisitedSiteAdminSectionIds((current) =>
          current.at(-1) === sectionId
            ? current
            : [...current.filter((id) => id !== sectionId).slice(-1), sectionId],
        );
      const feature = routedFeature();
      if (feature) {
        setActiveFeature(feature);
        setScreen('feature');
      } else {
        setScreen('home');
      }
    };
    window.addEventListener('popstate', onPopState);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      viewport.removeEventListener('change', syncViewport);
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isSystemFeedbackAdmin, role, routedFeature]);

  const renderSiteAdministrationSection = (sectionId: string) =>
    sectionId === 'system-management' && canUseSystemManagement ? (
      <SystemManagementWorkspace key={session.identity.id} repository={systemManagementRepository} />
    ) : sectionId === 'site-settings' ? (
      <AccountSettingsWorkspace key={governancePublicSiteId} initialSiteId={governancePublicSiteId || undefined} readOnly={activeScope.resource.kind === 'contractor'} />
    ) : sectionId === 'inspection-checklists' ? (
      <InspectionChecklistAdminWorkspace key={governancePublicSiteId} siteId={governancePublicSiteId} />
    ) : sectionId === 'organization-chart' ? (
      <OrganizationChartWorkspace key={`${governancePublicSiteId}:${activeScope.id}`} publicSiteId={governancePublicSiteId} contractorId={activeScope.resource.kind === 'contractor' ? activeScope.resource.contractorId : undefined} />
    ) : sectionId === 'work-location-registry' ? (
      <WorkLocationRegistryWorkspace key={governancePublicSiteId} publicSiteId={governancePublicSiteId} />
    ) : sectionId === 'my-info' ? (
      <MyInfoWorkspace auth={auth} />
    ) : sectionId === 'tbm-settings' && activeScope.resource.kind === 'contractor' ? (
      <TbmSettingsWorkspace
        key={`${activeScope.resource.siteId}:${activeScope.resource.contractorId}`}
        identityId={session.identity.id}
        siteId={activeScope.resource.siteId}
        contractorId={activeScope.resource.contractorId}
      />
    ) : sectionId === 'tbm-settings' && activeScope.resource.kind === 'site' ? (
      <TbmDisplaySettingsWorkspace key={governancePublicSiteId} siteId={governancePublicSiteId} actorId={session.identity.id} />
    ) : sectionId === 'user-approval' ? (
      <IdentityAdminWorkspace />
    ) : sectionId === 'contractor-management' ? (
      <ContractorWorkspace key={governancePublicSiteId} siteId={governancePublicSiteId} canManage={contractorManagementAuthorized} />
    ) : (
      <p className="site-admin-empty">선택한 관리 화면을 사용할 수 없습니다.</p>
    );

  return (
    <div
      className={`workspace-shell next-ia ${session.scopes.length === 1 ? 'single-scope' : ''} ${mobile ? 'is-mobile-layout' : ''} ${navigationOpen ? 'navigation-open' : ''} ${navigationPinned ? 'navigation-pinned' : ''} ${dark ? 'theme-dark' : ''}`}
    >
      <header className={mobile ? "mobile-account-layer" : "topbar"} hidden={mobile && !accountMenu}>
        {!mobile && <>
        <button
          className="nav-trigger"
          onClick={togglePersistentNavigation}
          aria-label={
            mobile
              ? navigationOpen
                ? '업무 메뉴 닫기'
                : '업무 메뉴 열기'
              : navigationPinned
                ? '탐색 메뉴 접기'
                : '탐색 메뉴 펼치기'
          }
          aria-expanded={mobile ? navigationOpen : navigationPinned}
        >
          <Menu size={20} />
        </button>
        <button className="topbar-home" onClick={closeFeature} aria-label="홈">
          <Home size={20} />
        </button>
        <HeadquartersSiteSwitcher session={session} onSelect={selectScope} />
        <button
          className="global-search"
          onClick={() => setCommand(true)}
          aria-label="업무 메뉴 검색"
        >
          <Search size={18} />
          <span>{isHeadquartersScope(activeScope) ? '업무 메뉴' : activeScope.label}</span>
        </button>
        <div className="top-actions">
          {topUtilityFeatures.map((feature) => {
            const Icon = directNavigationIcons[feature.id] ?? LayoutGrid;
            const label =
              feature.id === 'meeting-minutes'
                ? '회의'
                : feature.id === 'electronic-approval'
                  ? '결재'
                  : '자료실';
            return (
              <button
                key={feature.id}
                type="button"
                className="topbar-labelled topbar-direct-tool"
                data-feature-id={feature.id}
                aria-label={feature.label}
                title={feature.label}
                onPointerEnter={() => prefetchFeatureRoute(feature.id)}
                onFocus={() => prefetchFeatureRoute(feature.id)}
                onClick={() => openFeature(feature)}
              >
                <Icon size={17} />
                <span>{label}</span>
              </button>
            );
          })}
          <button
            type="button"
            className="topbar-feedback"
            onClick={openFeedback}
            aria-haspopup="dialog"
            aria-label="신고·건의"
            title="신고·건의"
          >
            <MessageSquareWarning size={18} />
          </button>
          <button
            onClick={() => {
              const opening = !notice;
              const notificationEpoch = identityNotificationScopeRef.current;
              if (notificationEpoch.scope !== identityNotificationScopeKey) return;
              setNotice(opening);
              if (opening && identityNotificationAuthorized)
                void identityNotificationPort().then(async (port) => {
                  if (!port) return;
                  const value = (await port.list()) as { items?: typeof identityNotifications };
                  if (identityNotificationScopeRef.current === notificationEpoch)
                    { setIdentityNotifications(value.items ?? []); setIdentityNotificationError(''); }
                }).catch(() => {
                  if (identityNotificationScopeRef.current === notificationEpoch)
                    setIdentityNotificationError('알림을 확인하지 못했습니다. 잠시 후 다시 열어 주세요.');
                });
            }}
            aria-label={identityUnreadCount ? `알림 ${identityUnreadCount}건` : '알림'}
          >
            <Bell size={18} />
            {identityNotificationAuthorized && identityUnreadCount > 0 && (
              <b className="notification-badge">{identityUnreadCount}</b>
            )}
          </button>
          <button
            className="profile"
            aria-label="내 정보 및 설정"
            title="내 정보 및 설정"
            aria-haspopup="menu"
            aria-expanded={accountMenu}
            onClick={() => {
              setAccountMenu((current) => !current);
            }}
          >
            <Settings size={18} />
          </button>
        </div>
        </>}
        {accountMenu && (
          <div className="account-popover" role="menu" aria-label="사용자 및 설정">
            <header>
              <span>
                <strong>{session.identity.displayName}</strong>
                <small>{session.identity.email}</small>
                <small>{productScopeRoleLabel(activeScope)}</small>
              </span>
              <button aria-label="사용자 및 설정 메뉴 닫기" onClick={() => setAccountMenu(false)}>
                <X size={17} />
              </button>
            </header>
            {session.scopes.length > 1 && (
              <div className="account-scope-list" role="listbox" aria-label="업무 범위 전환">
                {session.scopes.map((scope) => (
                  <button
                    role="option"
                    aria-selected={scope.id === activeScope.id}
                    key={scope.id}
                    onClick={() => selectScope(scope.id)}
                  >
                    <span>
                      <strong>{scope.label}</strong>
                      <small>{productScopeRoleLabel(scope)}</small>
                    </span>
                    {scope.id === activeScope.id && <Check size={17} />}
                  </button>
                ))}
              </div>
            )}
            {!mobile && siteAdministrationSections.length > 0 && (
              <button
                role="menuitem"
                data-site-admin-entry
                onPointerEnter={() => prefetchFeatureRoute(siteAdministrationSections[0]!.id)}
                onFocus={() => prefetchFeatureRoute(siteAdministrationSections[0]!.id)}
                onClick={() => openSiteAdministration(siteAdministrationSections[0]!.id)}
              >
                <Building2 size={17} />
                <span>
                  <strong>사업장 관리</strong>
                </span>
              </button>
            )}
            {!mobile && isSystemFeedbackAdmin && (
              <button role="menuitem" onClick={() => {
                setAccountMenu(false);
                openFeature(featureById('product-feedback'));
              }}>
                <MessageSquareWarning size={17} />
                <span><strong>시스템 개선 관리</strong></span>
              </button>
            )}
            {pwaState.installReady && (
              <button
                role="menuitem"
                onClick={() => {
                  void installPwa();
                  setAccountMenu(false);
                }}
              >
                <Download size={17} />
                <span>
                  <strong>이 기기에 설치</strong>
                  <small>앱 설치가 가능한 동안에만 표시됩니다.</small>
                </span>
              </button>
            )}
            <button
              role="menuitem"
              onClick={() => {
                void getTrustedMediaAdapter()
                  .purge(session.identity.id)
                  .finally(() => auth.signOut());
              }}
            >
              <LogOut size={17} />
              <span>
                <strong>로그아웃</strong>
              </span>
            </button>
          </div>
        )}
        {!mobile && notice && (
          <div className="notification">
            <strong>
              {identityNotificationError || (identityNotifications.length ? '사용자 승인 알림' : '새 알림이 없습니다')}
            </strong>
            {identityNotifications.map((item) => (
              <button
                key={item.notificationId}
                onClick={() => {
                  void identityNotificationPort().then((port) =>
                    port?.markRead(item.notificationId),
                  );
                  setIdentityUnreadCount((count) => Math.max(0, count - (item.unread ? 1 : 0)));
                  setNotice(false);
                  openSiteAdministration('user-approval');
                }}
              >
                <span>{item.displayNameSnapshot}</span>
                <small>사용자 승인 요청</small>
              </button>
            ))}
            <button onClick={() => setNotice(false)}>닫기</button>
          </div>
        )}
      </header>

      {!mobile && (
        <aside
          className="sidebar navigation-rail direct-navigation-rail"
          aria-label="업무 바로가기"
        >
          <nav aria-label="확정된 업무">
            {reviewedRailFeatures.map((feature) => {
              const Icon = directNavigationIcons[feature.id] ?? LayoutGrid;
              const badge = navigationBadgesByFeatureId.get(feature.id) ?? null;
              return (
                <button
                  key={feature.id}
                  data-feature-id={feature.id}
                  className={
                    screen === 'feature' && activeFeature.id === feature.id ? 'active' : ''
                  }
                  aria-label={badge ? `${feature.label}, ${badge.ariaLabel}` : feature.label}
                  title={feature.label}
                  onClick={() => openFeature(feature)}
                  onPointerEnter={() => prefetchFeatureRoute(feature.id)}
                  onFocus={() => prefetchFeatureRoute(feature.id)}
                >
                  <span className="direct-navigation-icon">
                    <Icon size={21} />
                    {badge && (
                      <b className="nav-count-badge" aria-hidden="true">
                        {badge.count}
                      </b>
                    )}
                  </span>
                  <span className="direct-navigation-label">{feature.label}</span>
                </button>
              );
            })}
            {reviewPendingRailFeatures.length > 0 && (
              <div className="direct-navigation-review-label">추가 업무</div>
            )}
            {reviewPendingRailFeatures.map((feature) => {
              const Icon = directNavigationIcons[feature.id] ?? LayoutGrid;
              const badge = navigationBadgesByFeatureId.get(feature.id) ?? null;
              return (
                <button
                  key={feature.id}
                  data-feature-id={feature.id}
                  data-navigation-placement="additional"
                  className={
                    screen === 'feature' && activeFeature.id === feature.id ? 'active' : ''
                  }
                  aria-label={badge ? `${feature.label}, ${badge.ariaLabel}` : feature.label}
                  title={feature.label}
                  onClick={() => openFeature(feature)}
                  onPointerEnter={() => prefetchFeatureRoute(feature.id)}
                  onFocus={() => prefetchFeatureRoute(feature.id)}
                >
                  <span className="direct-navigation-icon">
                    <Icon size={21} />
                    {badge && (
                      <b className="nav-count-badge" aria-hidden="true">
                        {badge.count}
                      </b>
                    )}
                  </span>
                  <span className="direct-navigation-label">{feature.label}</span>
                </button>
              );
            })}
            {bottomRailFeatures.map((feature) => {
              const Icon = directNavigationIcons[feature.id] ?? LayoutGrid;
              const badge = navigationBadgesByFeatureId.get(feature.id) ?? null;
              return (
                <button
                  key={feature.id}
                  data-feature-id={feature.id}
                  data-navigation-placement="bottom"
                  className={
                    screen === 'feature' && activeFeature.id === feature.id ? 'active' : ''
                  }
                  aria-label={badge ? `${feature.label}, ${badge.ariaLabel}` : feature.label}
                  title={feature.label}
                  onClick={() => openFeature(feature)}
                  onPointerEnter={() => prefetchFeatureRoute(feature.id)}
                  onFocus={() => prefetchFeatureRoute(feature.id)}
                >
                  <span className="direct-navigation-icon">
                    <Icon size={21} />
                    {badge && (
                      <b className="nav-count-badge" aria-hidden="true">
                        {badge.count}
                      </b>
                    )}
                  </span>
                  <span className="direct-navigation-label">{feature.label}</span>
                </button>
              );
            })}
          </nav>
          <div className="rail-footer">
            <ThemeToggle dark={dark} setDark={setDark} />
          </div>
        </aside>
      )}

      {mobile && navigationOpen && (
        <>
          <button
            className="mobile-navigation-backdrop"
            aria-label="모바일 업무 메뉴 닫기"
            onClick={() => setNavigationOpen(false)}
          />
          <aside className="mobile-navigation-sheet" aria-label="모바일 업무 메뉴">
            <header>
              <strong>업무 메뉴</strong>
              <button aria-label="모바일 업무 메뉴 닫기" onClick={() => setNavigationOpen(false)}>
                <X size={18} />
              </button>
            </header>
            <div>
              <section>
                <h2>업무 바로가기</h2>
                {directRailFeatures.map((feature) => {
                  const Icon = directNavigationIcons[feature.id] ?? LayoutGrid;
                  return (
                    <button
                      key={feature.id}
                      data-feature-id={feature.id}
                      data-active={
                        (screen === 'feature' && activeFeature.id === feature.id) || undefined
                      }
                      onClick={() => openFeature(feature)}
                      onPointerEnter={() => prefetchFeatureRoute(feature.id)}
                      onFocus={() => prefetchFeatureRoute(feature.id)}
                    >
                      <span>
                        <Icon size={17} />
                        {feature.label}
                      </span>
                      <ChevronRight size={16} />
                    </button>
                  );
                })}
              </section>
              {topUtilityFeatures.length > 0 && (
                <section>
                  <h2>보조 도구</h2>
                  {topUtilityFeatures.map((feature) => (
                    <button key={feature.id} onClick={() => openFeature(feature)}>
                      <span>{feature.label}</span>
                      <ChevronRight size={16} />
                    </button>
                  ))}
                </section>
              )}
              <div className="rail-footer">
                <ThemeToggle dark={dark} setDark={setDark} />
              </div>
            </div>
          </aside>
        </>
      )}

      <main className={mobile ? 'workspace-main mobile-main' : 'workspace-main'}>
        {featureAccessState.kind === 'malformed' && (
          <div className="workspace-route-loading" role="alert">
            기능 설정을 불러오지 못했습니다. 로그인은 유지됩니다. 새로고침 후 다시 확인해 주세요.
          </div>
        )}
        {routePending && (
          <div className="workspace-route-loading" role="status" aria-live="polite">
            업무 화면을 여는 중입니다.
          </div>
        )}
        <div className="workspace-watermark" aria-hidden="true" />
        {screen === 'home' &&
          (mobile ? (
            <MobileHome
              displayName={session.identity.displayName}
              scopeLabel={activeScope.label}
              contractorView={activeScope.resource.kind === 'contractor'}
              inspectionContent={mobileInspectionTasks}
              onAccount={() => setAccountMenu(true)}
              openChecklist={openMobileChecklist}
              offline={pwaState.offline}
              availableFeatureIds={mobileFieldFeatureIds}
              openReport={openFeedback}
              openFeature={openFeature}
              openQuickInspection={openQuickInspection}
              openFindings={() => openFindingCenter({ status: 'unresolved' })}
            />
          ) : (
            <RoleHome
              role={role}
              homeScope={activeScope.resource}
              homeFeatureIds={roleFeatures.map(feature=>feature.id)}
              onInspection={period=>{
                if (!roleFeatures.some(feature=>feature.id==='inspection-reporting')) return;
                if (period===null) { openFindingCenter({status:'unresolved'}); return; }
                openFeature(featureById('inspection-reporting'));
                setHomeInspectionPeriod(period);
              }}
              workHomeSnapshot={workHomeSnapshot.state==='loaded'&&workHomeSnapshot.projection.selectedScopeId!==workHomeScopeId?{state:'unavailable'}:workHomeSnapshot}
              openFeature={openFeature}
              favorites={favoriteFeatures}
              openCommand={() => setCommand(true)}
              boardAuthority={boardAuthority}
              boardOwner={{
                uid: session.identity.id,
                contractorId:
                  activeScope.resource.kind === 'contractor'
                    ? activeScope.resource.contractorId
                    : null,
              }}
            />
          ))}
        {screen === 'feature' && mobile && !isFieldMobileFeature(activeFeature.id) && (
          <section className="workspace-route-loading" role="status">
            <p>이 기능은 PC에서 이용해 주세요.</p>
            <Button onClick={closeFeature}>모바일 홈으로</Button>
          </section>
        )}
        {screen === 'feature' && (!mobile || isFieldMobileFeature(activeFeature.id)) &&
          (['inspection-log', 'finding-action'].includes(activeFeature.id) ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="ops-page inspection-entry"
                  back="ops-back"
                  masthead="ops-masthead"
                  label="점검 화면 불러오는 중"
                  lead={false}
                  panels={3}
                />
              }
            >
              <InspectionWorkflow
                role={role}
                identity={{ id: session.identity.id, name: session.identity.displayName }}
                persistenceContext={inspectionPersistenceContext}
                view={activeFeature.id === 'finding-action' ? 'findings' : 'entry'}
                findingPreset={findingCenterPreset ?? undefined}
                findingActionHandlers={{
                  'open-approval': () => openFeature(featureById('electronic-approval')),
                }}
                onBack={
                  activeFeature.id === 'finding-action'
                    ? openInspectionDashboard
                    : () => setScreen('home')
                }
              />
            </Suspense>
          ) : activeFeature.id === 'electronic-approval' ? (
            <Suspense
              fallback={
                <section className="approval-hub" data-workspace-template="inspection">
                  <HubHeader title="전자결재" onBack={closeFeature} />
                  <FeatureLoading label="결재 문서 불러오는 중" />
                </section>
              }
            >
              <ApprovalHub
                ports={approvalPorts}
                initialRequestId={approvalRequestToOpen}
                query={{
                  actorIdentityId: session.identity.id,
                  scope: approvalScope,
                }}
                onBack={closeFeature}
              />
            </Suspense>
          ) : activeFeature.id === 'work-permit' ? (
            <Suspense
              fallback={
                <section className="work-ledger-workspace" data-workspace-dashboard>
                  <FeatureLoading label="비일상작업 불러오는 중" />
                </section>
              }
            >
              <WorkLedgerWorkspace
                role={role}
                scope={headquartersWorkScope(activeScope.resource, role, session.identity.id, headquartersReadContext)}
                identityId={session.identity.id}
                identityName={session.identity.displayName}
                onOpenApproval={(requestId) => {
                  openFeature(featureById('electronic-approval'));
                  setApprovalRequestToOpen(requestId);
                }}
                onBack={() => setScreen('home')}
              />
            </Suspense>
          ) : activeFeature.id === 'equipment-dispatch' && equipmentDispatchActorProjection ? (
            <Suspense fallback={<FeatureLoading label="차량·장비 작업배차 불러오는 중" />}>
              <EquipmentDispatchWorkspace
                identity={{ id: session.identity.id, name: session.identity.displayName }}
                resource={activeScope.resource}
                projection={equipmentDispatchActorProjection}
                onBack={closeFeature}
              />
            </Suspense>
          ) : activeFeature.id === 'incident' ? (
            <Suspense fallback={<FeatureLoading label="재해자 관리대장 불러오는 중" />}>
              <VictimRegistryWorkspace
                identity={{ id: session.identity.id, name: session.identity.displayName }}
                scope={activeScope.resource}
                siteId={governancePublicSiteId}
                capabilities={activeScope.capabilities}
                featureCanAuthor={enforcedIncidentAuthor}
                featureCanCreateRisk={enforcedRiskAuthor}
                contractorName={activeScope.resource.kind === 'contractor' ? activeScope.label : ''}
                onBack={closeFeature}
                onStartRiskReassessment={(source) => {
                  openFeature(featureById('regular-risk'));
                  setIncidentRiskSource(source);
                }}
                onOpenRiskReassessment={(assessmentId) => {
                  openFeature(featureById('regular-risk'));
                  setIncidentAssessmentId(assessmentId);
                }}
              />
            </Suspense>
          ) : activeFeature.id === 'evacuation-drill' ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="event-workspace feature-page"
                  masthead="event-masthead"
                  leading
                  label="안전 기록 불러오는 중"
                  nav={2}
                  panels={3}
                />
              }
            >
              <EmergencyDrillWorkspace
                identity={{
                  id: session.identity.id,
                  name: session.identity.displayName,
                }}
                scope={activeScope.resource}
                onBack={closeFeature}
              />
            </Suspense>
          ) : activeFeature.id === 'tbm' ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="tbm-workspace"
                  label="TBM 화면을 불러오는 중"
                  panels={3}
                />
              }
            >
              <TbmWorkspace
                role={role}
                identity={{ id: session.identity.id, name: session.identity.displayName }}
                scope={tbmPersistenceContext}
                contractorName={activeScope.resource.kind === 'contractor' ? activeScope.label : ''}
                onBack={closeFeature}
              />
            </Suspense>
          ) : activeFeature.id === 'shared-drive' ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="shared-drive-workspace"
                  back="back-link"
                  label="안전보건자료실 불러오는 중"
                  split
                  panels={2}
                  rows={5}
                />
              }
            >
              <SharedDriveWorkspace resource={activeScope.resource} onBack={closeFeature} initialReadContext={headquartersReadContext} />
            </Suspense>
          ) : activeFeature.id === 'regular-risk' ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="risk-round-workspace"
                  masthead="risk-round-header"
                  leading
                  label="위험성평가 불러오는 중"
                  lead={false}
                  panels={3}
                />
              }
            >
              <RiskAssessmentWorkspace
                key={activeFeature.id}
                incidentSource={incidentRiskSource}
                onIncidentSourceConsumed={() => setIncidentRiskSource(undefined)}
                initialAssessmentId={incidentAssessmentId}
                onInitialAssessmentConsumed={() => setIncidentAssessmentId(undefined)}
                kind="regular"
                onOpenApprovals={() => openFeature(featureById('electronic-approval'))}
                role={role}
                identity={{ id: session.identity.id, name: session.identity.displayName }}
                scope={activeScope.resource}
                onBack={closeFeature}
              />
            </Suspense>
          ) : activeFeature.id === 'chemical-msds-management' ? (
            <Suspense fallback={<FeatureLoading label="MSDS 관리대장을 불러오는 중" />}>
              <ChemicalMsdsWorkspace
                onBack={closeFeature}
                siteId={governancePublicSiteId}
                organizationName={activeScope.label}
                reviewerName={session.identity.displayName}
                accessMode={role === 'partner' ? 'contractor' : 'reviewer'}
                contractorId={
                  activeScope.resource.kind === 'contractor'
                    ? activeScope.resource.contractorId
                    : null
                }
              />
            </Suspense>
          ) : activeFeature.id === 'contractor-management' ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="contractor-workspace"
                  back="back-link"
                  masthead="contractor-masthead"
                  label="업체 정보 불러오는 중"
                  split
                  panels={2}
                  rows={5}
                />
              }
            >
              <ContractorWorkspace
                key={governancePublicSiteId}
                siteId={governancePublicSiteId}
                canManage={contractorManagementAuthorized}
                onBack={closeFeature}
              />
            </Suspense>
          ) : activeFeature.id === 'safety-training' ? (
            <Suspense
              fallback={
                <div className="education-workspace" data-workspace-dashboard>
                  <FeatureLoading label="교육 기록 불러오는 중" />
                </div>
              }
            >
              <SafetyEducationWorkspace
                key={activeScope.resource.kind === 'tenant' ? 'tenant' : activeScope.resource.siteId}
                role={role}
                identity={{ id: session.identity.id, name: session.identity.displayName }}
                scope={activeScope.resource}
                onBack={closeFeature}
              />
            </Suspense>
          ) : activeFeature.id === 'safety-content' ? (
            <Suspense
              fallback={
                <section
                  className="safety-content-workspace"
                  data-workspace-dashboard
                  data-workspace-template="inspection"
                >
                  <FeatureLoading label="안전 콘텐츠 불러오는 중" />
                </section>
              }
            >
              <SafetyContentWorkspace
                key={
                  activeScope.resource.kind === 'tenant'
                    ? 'tenant'
                    : activeScope.resource.siteId === 'eumseong'
                      ? 'site-eumseong'
                      : activeScope.resource.siteId
                }
                siteId={
                  activeScope.resource.kind === 'tenant'
                    ? ''
                    : activeScope.resource.siteId === 'eumseong'
                      ? 'site-eumseong'
                      : activeScope.resource.siteId
                }
                role={role}
                identityId={session.identity.id}
                onBack={closeFeature}
              />
            </Suspense>
          ) : [
              'inspection-reporting',
              'inspection-unresolved',
            ].includes(activeFeature.id) ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="inspection-read-workspace"
                  back="inspection-read-back"
                  label="점검 조회 자료 불러오는 중"
                  panels={3}
                  rows={4}
                />
              }
            >
              <InspectionReadWorkspace
                key={`${activeScope.resource.kind === 'tenant' ? 'tenant' : activeScope.resource.siteId}:${homeInspectionPeriod ? `${homeInspectionPeriod.from}:${homeInspectionPeriod.to}` : 'default'}`}
                siteId={
                  activeScope.resource.kind === 'tenant'
                    ? null
                    : activeScope.resource.siteId === 'eumseong'
                      ? 'site-eumseong'
                      : activeScope.resource.siteId
                }
                initialPeriod={homeInspectionPeriod ?? undefined}
                initialJournalOpen={Boolean(homeInspectionPeriod)}
                role={role}
                persona={activeScope.persona}
                identity={{ id: session.identity.id, name: session.identity.displayName }}
                initialView={
                  activeFeature.id === 'inspection-unresolved'
                    ? 'unresolved'
                    : 'overview'
                }
                onStart={
                  ['client', 'partner', 'siteLead'].includes(role)
                    ? (preset) =>
                        setInspectionEntryPreset(
                          preset ?? {
                            category: 'structured',
                            structuredKind: '합동점검',
                          },
                        )
                    : undefined
                }
                onFindings={
                  canAccessFeature(role, featureById('finding-action'))
                    ? openFindingCenter
                    : undefined
                }
                onBack={closeFeature}
              />
              {inspectionEntryPreset && (
                <InspectionWorkflow
                  role={role}
                  identity={{ id: session.identity.id, name: session.identity.displayName }}
                  persistenceContext={inspectionPersistenceContext}
                  initialPreset={inspectionEntryPreset}
                  onBack={() => setInspectionEntryPreset(null)}
                />
              )}
            </Suspense>
          ) : activeFeature.id === 'safety-cost' ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="budget-workspace"
                  back="budget-back"
                  masthead="budget-masthead"
                  label="예산·자재 업무 불러오는 중"
                  panels={3}
                />
              }
            >
              <BudgetMaterialsWorkspace
                mode="budget"
                role={role}
                identity={{ id: session.identity.id, name: session.identity.displayName }}
                siteId={activeScope.resource.kind === 'tenant' ? null : activeScope.resource.siteId}
                contractorId={
                  activeScope.resource.kind === 'contractor'
                    ? activeScope.resource.contractorId
                    : null
                }
                onBack={closeFeature}
              />
            </Suspense>
          ) : activeFeature.id === 'site-settings' ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="account-settings"
                  back="account-settings__back"
                  masthead="account-settings__header"
                  label="계정·사업장 설정 원장 불러오는 중"
                  panels={3}
                />
              }
            >
              <AccountSettingsWorkspace key={governancePublicSiteId} initialSiteId={governancePublicSiteId || undefined} onBack={closeFeature} readOnly={activeScope.resource.kind === 'contractor'} />
            </Suspense>
          ) : activeFeature.id === 'organization-chart' ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="ops-page"
                  back="ops-back"
                  masthead="ops-masthead"
                  label="조직도 불러오는 중"
                  lead={false}
                  panels={2}
                  rows={4}
                />
              }
            >
              <OrganizationChartWorkspace
                key={`${governancePublicSiteId}:${activeScope.id}`}
                contractorId={activeScope.resource.kind === 'contractor' ? activeScope.resource.contractorId : undefined}
                onBack={closeFeature}
                publicSiteId={governancePublicSiteId}
              />
            </Suspense>
          ) : activeFeature.id === 'work-location-registry' ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="ops-page"
                  back="ops-back"
                  masthead="ops-masthead"
                  label="구역·작업장소 대장 불러오는 중"
                  lead={false}
                  panels={2}
                />
              }
            >
              <WorkLocationRegistryWorkspace
                key={governancePublicSiteId}
                onBack={closeFeature}
                publicSiteId={governancePublicSiteId}
              />
            </Suspense>
          ) : activeFeature.id === 'user-approval' ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="identity-admin-workspace"
                  back="identity-admin-back"
                  label="사용자 승인 자료 불러오는 중"
                  split
                  panels={2}
                  rows={5}
                />
              }
            >
              <IdentityAdminWorkspace onBack={closeFeature} />
            </Suspense>
          ) : activeFeature.id === 'product-feedback' && isSystemFeedbackAdmin ? (
            <Improvements
              identity={{ id: session.identity.id, name: session.identity.displayName }}
              scope={{ kind: 'tenant', tenantId: 'kop' }}
                canTriage={isSystemFeedbackAdmin}
            />
          ) : activeFeature.id === 'meeting-minutes' ? (
            <Suspense fallback={<FeatureLoading label="회의록 불러오는 중" />}>
              <MeetingMinutesWorkspace
                key={activeScope.resource.kind === 'tenant' ? 'tenant' : activeScope.resource.siteId}
                siteId={activeScope.resource.kind === 'tenant' ? '' : activeScope.resource.siteId === 'eumseong' ? 'site-eumseong' : activeScope.resource.siteId}
                identity={{
                  id: session.identity.id,
                  name: session.identity.displayName,
                  contractorId:
                    activeScope.resource.kind === 'contractor'
                      ? activeScope.resource.contractorId
                      : null,
                }}
                onBack={closeFeature}
              />
            </Suspense>
          ) : activeFeature.id === 'heat-index' ? (
            <Suspense
              fallback={
                <FeatureSkeleton
                  screenClassName="heat-workspace"
                  masthead="heat-hero"
                  leading
                  label="체감온도 기록 불러오는 중"
                  panels={3}
                />
              }
            >
              <HeatIndexWorkspace
                siteId={governancePublicSiteId}
                identity={{ id: session.identity.id, name: session.identity.displayName }}
                onBack={closeFeature}
              />
            </Suspense>
          ) : (
            <FeaturePage role={role} feature={activeFeature} close={() => setScreen('home')} />
          ))}
        {screen === 'mobileHome' && (
          <MobileHome
            onAccount={() => setAccountMenu(true)}
            displayName={session.identity.displayName}
            scopeLabel={activeScope.label}
            contractorView={activeScope.resource.kind === 'contractor'}
            inspectionContent={mobileInspectionTasks}
            openChecklist={openMobileChecklist}
            offline={pwaState.offline}
            availableFeatureIds={mobileFieldFeatureIds}
            openReport={openFeedback}
            openFeature={openFeature}
            openQuickInspection={openQuickInspection}
            openFindings={() => openFindingCenter({ status: 'unresolved' })}
          />
        )}
        {screen === 'inspection' && (
          <Suspense
            fallback={
              <FeatureSkeleton
                screenClassName="ops-page inspection-entry"
                back="ops-back"
                masthead="ops-masthead"
                label="점검 화면 불러오는 중"
                lead={false}
                panels={3}
              />
            }
          >
            <InspectionWorkflow
              role={role}
              identity={{ id: session.identity.id, name: session.identity.displayName }}
              persistenceContext={inspectionPersistenceContext}
              initialPreset={inspectionEntryPreset ?? undefined}
              onBack={() => setScreen('home')}
            />
          </Suspense>
        )}
      </main>
      {entitledSiteAdminSectionId && (
        <SiteAdministrationDialog
          sections={siteAdministrationSections}
          activeSectionId={entitledSiteAdminSectionId}
          onSelectSection={openSiteAdministration}
          onPrefetchSection={prefetchFeatureRoute}
          onClose={closeSiteAdministration}
        >
          {visitedSiteAdminSectionIds.map((sectionId) => {
            const section = siteAdministrationSections.find((item) => item.id === sectionId);
            if (!section) return null;
            const active = sectionId === entitledSiteAdminSectionId;
            return (
              <div
                key={sectionId}
                className="site-admin-section-panel"
                hidden={!active}
                aria-hidden={!active}
              >
                <Suspense fallback={<SiteAdministrationLoading label={section.label} />}>
                  {renderSiteAdministrationSection(sectionId)}
                </Suspense>
              </div>
            );
          })}
        </SiteAdministrationDialog>
      )}
      {feedbackOpen && (
        <FeedbackDialog
          key={`${session.identity.id}:${operationalSelectedScopeId(activeScope.resource)}`}
          identity={{ id: session.identity.id, name: session.identity.displayName }}
          scope={activeScope.resource}
          route={feedbackRoute}
          initialView={feedbackInitialView}
          onClose={closeFeedback}
        />
      )}

      {command && (
        <div className="command-backdrop" onMouseDown={() => setCommand(false)}>
          <section
            className="command-panel"
            role="dialog"
            aria-modal="true"
            aria-label="업무 검색"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <label>
                <Search size={18} />
                <input
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="업무 메뉴 검색"
                />
              </label>
              <button aria-label="검색 닫기" onClick={() => setCommand(false)}>
                <X size={19} />
              </button>
            </header>
            <div>
              {results.slice(0, 10).map(({ feature, category, description, favoritable }) => (
                <article className="command-result" key={feature.id}>
                  <button
                    className="command-result-open"
                    onPointerEnter={() => prefetchFeatureRoute(feature.id)}
                    onFocus={() => prefetchFeatureRoute(feature.id)}
                    onClick={() => {
                      openFeature(feature);
                      setCommand(false);
                    }}
                  >
                    <span>
                      <strong>{feature.label}</strong>
                      <small>{description}</small>
                    </span>
                    <em>{category}</em>
                    <ChevronRight size={16} />
                  </button>
                  {favoritable && favoriteIds.includes(feature.id) && (
                    <span className="command-order" aria-label={`${feature.label} 순서 변경`}>
                      <button
                        aria-label={`${feature.label} 왼쪽으로 이동`}
                        disabled={favoriteIds.indexOf(feature.id) === 0}
                        onClick={() => reorderFavorite(feature.id, -1)}
                      >
                        <ChevronLeft size={15} />
                      </button>
                      <button
                        aria-label={`${feature.label} 오른쪽으로 이동`}
                        disabled={favoriteIds.indexOf(feature.id) === favoriteIds.length - 1}
                        onClick={() => reorderFavorite(feature.id, 1)}
                      >
                        <ChevronRight size={15} />
                      </button>
                    </span>
                  )}
                  {favoritable && (
                    <button
                      className="command-favorite"
                      aria-label={`${feature.label} 즐겨찾기 ${favoriteIds.includes(feature.id) ? '제거' : '추가'}`}
                      aria-pressed={favoriteIds.includes(feature.id)}
                      onClick={() => toggleFavorite(feature.id)}
                    >
                      <Star
                        size={18}
                        fill={favoriteIds.includes(feature.id) ? 'currentColor' : 'none'}
                      />
                    </button>
                  )}
                </article>
              ))}
              {results.length === 0 && <p className="command-empty">검색 결과가 없습니다.</p>}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export function WorkspaceApp() {
  const meetingPreviewEnabled =
    import.meta.env.DEV &&
    ['true', '1'].includes(import.meta.env.VITE_LOCAL_PREVIEW ?? '') &&
    ['127.0.0.1', 'localhost', '::1'].includes(window.location.hostname) &&
    window.location.pathname === '/preview/meetings';
  useEffect(() => {
    const normalizedPath = normalizeWorkspacePath(window.location.pathname);
    if (normalizedPath !== window.location.pathname)
      window.history.replaceState({}, '', normalizedPath);
  }, []);
  if (meetingPreviewEnabled) {
    return (
      <Suspense fallback={<FeatureLoading label="회의록 불러오는 중" />}>
        <MeetingMinutesWorkspace
          siteId="site-eumseong"
          identity={{
            id: 'preview-eumseong-safety',
            name: '음성공장 안전관리자',
            contractorId: null,
          }}
          onBack={() => window.history.pushState({}, '', '/')}
        />
      </Suspense>
    );
  }
  return (
    <AuthBoundary>
      {(session, auth) => <AuthenticatedWorkspace session={session} auth={auth} />}
    </AuthBoundary>
  );
}
